import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Edit, Loader2, Eye, EyeOff } from "lucide-react";
import { UserRole, getRoleDisplayName } from "@/types/auth";
import { useAuth } from "@/hooks/useAuth";
import { useUpdateUsuario } from "@/hooks/useUsuarios";
import { toast } from "sonner";

interface Usuario {
  id: string;
  email: string;
  nome: string;
  role: UserRole;
  user_role: UserRole;
  setor_departamento?: 'vendas' | 'financeiro' | 'tecnico' | 'administrativo';
  ativo: boolean;
  telefone?: string;
}

interface EditarUsuarioDialogProps {
  usuario: Usuario;
  onUsuarioUpdated: () => void;
}

export function EditarUsuarioDialog({ usuario, onUsuarioUpdated }: EditarUsuarioDialogProps) {
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { profile, canManageUsers } = useAuth();
  const updateUsuario = useUpdateUsuario();
  
  const [formData, setFormData] = useState({
    nome: usuario.nome,
    email: usuario.email,
    role: usuario.role,
    setor_departamento: usuario.setor_departamento || undefined,
    telefone: usuario.telefone || "",
    ativo: usuario.ativo,
    novaSenha: ""
  });

  // Verificar se o usuário atual pode editar outros usuários
  const canEditUsers = canManageUsers();
  const isEditingSelf = profile?.id === usuario.id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Preparar dados para atualização
      const updateData = {
        id: usuario.id,
        nome: formData.nome,
        email: formData.email,
        role: formData.role,
        setor_departamento: formData.setor_departamento,
        telefone: formData.telefone,
        ativo: formData.ativo
      };

      // Se uma nova senha foi fornecida, incluí-la na atualização
      if (formData.novaSenha.trim()) {
        (updateData as any).novaSenha = formData.novaSenha;
      }

      await updateUsuario.mutateAsync(updateData);
      
      setOpen(false);
      onUsuarioUpdated();
      
      // Limpar senha após salvar
      setFormData(prev => ({ ...prev, novaSenha: "" }));
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
    }
  };

  const handleReset = () => {
    setFormData({
      nome: usuario.nome,
      email: usuario.email,
      role: usuario.role,
      setor_departamento: usuario.setor_departamento || undefined,
      telefone: usuario.telefone || "",
      ativo: usuario.ativo,
      novaSenha: ""
    });
    setShowPassword(false);
  };

  if (!canEditUsers) {
    return null; // Não mostrar o botão se não tiver permissão
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar Usuário</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="nome">Nome</Label>
            <Input
              id="nome"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="telefone">Telefone</Label>
            <Input
              id="telefone"
              value={formData.telefone}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
            />
          </div>
          
          <div>
            <Label htmlFor="role">Role</Label>
            <Select 
              value={formData.role} 
              onValueChange={(value: UserRole) => 
                setFormData({ ...formData, role: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">{getRoleDisplayName('admin')}</SelectItem>
                <SelectItem value="operator">{getRoleDisplayName('operator')}</SelectItem>
                <SelectItem value="viewer">{getRoleDisplayName('viewer')}</SelectItem>
                <SelectItem value="vendedor">{getRoleDisplayName('vendedor')}</SelectItem>
                <SelectItem value="vendedor_interno">{getRoleDisplayName('vendedor_interno')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="setor">Setor</Label>
            <Select 
              value={formData.setor_departamento || ""} 
              onValueChange={(value: "vendas" | "financeiro" | "tecnico" | "administrativo") => 
                setFormData({ ...formData, setor_departamento: value || undefined })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um setor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vendas">Vendas</SelectItem>
                <SelectItem value="financeiro">Financeiro</SelectItem>
                <SelectItem value="tecnico">Técnico</SelectItem>
                <SelectItem value="administrativo">Administrativo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="novaSenha">Nova Senha</Label>
            <div className="relative">
              <Input
                id="novaSenha"
                type={showPassword ? "text" : "password"}
                value={formData.novaSenha}
                onChange={(e) => setFormData({ ...formData, novaSenha: e.target.value })}
                placeholder="Deixe em branco para manter a senha atual"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Deixe em branco para manter a senha atual
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="ativo"
              checked={formData.ativo}
              onCheckedChange={(checked) => setFormData({ ...formData, ativo: checked })}
            />
            <Label htmlFor="ativo">Usuário Ativo</Label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleReset}
              disabled={updateUsuario.isPending}
            >
              Resetar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={updateUsuario.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={updateUsuario.isPending}>
              {updateUsuario.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 